<template>
  <div
    class="w-full internal-review-form flex flex-col"
    :class="{
      'internal-review-form--full': !mobilePreview && !desktopPreview,
      'pointer-events-none': mobilePreview || desktopPreview,
    }"
  >
    <hosted-page-banner
      :brand-name="brandNameLocal"
      :brand-colour="brandColourLocal"
      :icon-path="iconPathLocal"
      :logo-path="logoPathLocal"
      :prefer-logo-over-icon="preferLogoOverIconLocal"
      :mobile-preview="mobilePreview"
      :desktop-preview="desktopPreview"
    >
      <h4
        v-if="!sent"
        class="text-center text-inherit w-full mt-5 lg:text-3xl"
        v-html="$t('Title', { businessName: brandNameLocal })"
      >
      </h4>
      <p
            v-if="!sent"
            class="w-full text-center text-inherit mt-5 text-lg md:text-xl transition-opacity duration-300">
            {{ $t('Subtitle') }}
      </p>
    </hosted-page-banner>

    <div
      class="w-full bg-white p-base flex justify-center flex-grow"
      :class="{ 'items-center': !shouldShowForm || sent }"
    >
      <form
        class="w-full sm:w-2/3 xl:w-2/5 transition-all duration-500 ease-in-out"
        :class="{ 'mt-auto mb-auto': !shouldShowForm || sent }"
        @submit.prevent
      >
        <div class="stars-container transition-all duration-500 ease-in-out"
          :class="{ 
            'transform-center': !shouldShowForm || sent,
            'transform-top': shouldShowForm && !sent
          }">
      
      <!-- <h5
            v-if="!sent"
            class="w-full text-center text-inherit mt-3 md:text-2xl lg:text-3xl transition-opacity duration-300">
            {{ $t('Subtitle') }}
      </h5> -->


          <div class="flex justify-center my-10">
            <star-rating
              v-model="rating"
              :increment="1"
              :round-start-rating="true"
              :show-rating="false"
              :rounded-corners="true"
              :read-only="sent"
              :active-color="accentColourLocal"
              :star-size="40"
              @rating-selected="ratingSelected"/>
          </div>

          <!-- Rating message -->
          <div v-if="rating > 0 && !sent" class="w-full text-center mt-3 transition-all duration-300">
            <h6 class="md:text-xl lg:text-2xl font-semibold">
              {{ ratingMessage }}
            </h6>
          </div>
        </div>

        <template v-if="sent">
          <transition name="fade-slide" appear>
            <div class="animate-content">
              <h6
                class="w-full text-center md:text-2xl lg:text-3xl mt-3 font-semibold">
                {{ $t('ThanksMsgTitle') }}
              </h6>

              <template v-if="rating > 3 && googleReviewLink">
                <p
                  class="text-black mt-3 text-center font-medium">
                  {{ $t('GoogleReviewMsg') }}
                </p>

                <div class="mt-3 w-full flex justify-center ">
                  <google-button
                    :text="$t('GoogleReviewButton')"
                    class="md:w-1/2 xl:w-2/5"
                    rounded
                    min-width="250px"
                    @click="goToGoogleReview"
                  >
                  </google-button>
                </div>
              </template>

              <template v-else>
                <p
                  class="text-black mt-3 text-center font-medium">
                  {{ $t('ThanksMsgSubtitle') }}
                </p>
              </template>
            </div>
          </transition>
        </template>

        <template v-else>
          <!-- Transition wrapper for all rating-dependent content -->
          <transition name="fade-slide" mode="out-in">
            <!-- Empty state for smooth initial transition -->
            <div v-if="rating === 0" key="empty-state" class="animate-content">
              <!-- Placeholder to maintain layout -->
            </div>
            
            <!-- Scenario 2: 4+ stars - Show Google Review option first -->
            <div v-else-if="rating > 3 && googleReviewLink && !showInternalForm" 
              class="mt-5 text-center animate-content"
              key="google-review">
              <div class="mt-3 w-full flex justify-center">
                <google-button
                  :text="$t('GoogleReviewButton')"
                  class="md:w-1/2 xl:w-2/5"
                  rounded
                  min-width="250px"
                  @click="goToGoogleReview"
                >
                </google-button>
              </div>
              <div class="mt-3">
                <a @click="showInternalForm = true" class="cursor-pointer text-sm link-contextual">
                  {{ $t('LeaveInternalReviewInstead') }}
                </a>
              </div>
            </div>

            <!-- Scenario 1: 3 stars or less OR user opts for internal review -->
            <div
              v-else-if="shouldShowForm"
              key="internal-form"
              class="form-content animate-content">
              <div class="vx-row mt-5">
                <div class="vx-col w-full">
                <vs-textarea
                  v-model="feedback"
                  class="w-full"
                  :placeholder="$t('FeedbackPlaceholder')"
                  :name="'feedback'"
                  rows="3"
                  />
                </div>
              </div>

              <div class="flex flex-row gap-x-4 mt-5">
                <div class="w-1/2">
                <vs-input
                  v-model="firstName"
                  class="w-full required"
                  :label="$t('FirstNameLabel')"
                  :name="$t('FirstNameLabel')"
                  autocomplete="given-name"
                  v-validate="'required|max:50'"
                  data-vv-validate-on="blur|input"
                  :danger="errors.has($t('FirstNameLabel'))"
                  :danger-text="errors.first($t('FirstNameLabel'))"
                  val-icon-danger="clear"/>
                </div>

                <div class="w-1/2">
                <vs-input
                  v-model="lastName"
                  class="w-full"
                  :label="$t('LastNameLabel')"
                  :name="$t('LastNameLabel')"
                  autocomplete="family-name"
                  v-validate="'max:50'"
                  data-vv-validate-on="blur|input"
                  :danger="errors.has($t('LastNameLabel'))"
                  :danger-text="errors.first($t('LastNameLabel'))"
                  val-icon-danger="clear"/>
                </div>
              </div>

              <!-- Contact method group with visual grouping -->
              <fieldset
            class="mt-5 border border-solid rounded-lg p-4"
            :class="contactValidationError ? 'border-danger' : 'border-grey-light'">
            <legend class="px-2 text-sm">
              <span :class="contactValidationError
              ? 'text-danger'
              : 'text-gray-700 font-medium'">Enter email or phone number</span>
              <span class="text-danger">&nbsp;*</span>
            </legend>
            <div class="vx-row pt-2">
              <div class="vx-col w-full md:w-1/2 md:pr-2">
                <vs-input
                  v-model="email"
                  type="email"
                  class="w-full"
                  :label="$t('EmailLabel')"
                  :name="$t('EmailLabel')"
                  autocomplete="email"
                  v-validate="{ rules: { email: true } }"
                  data-vv-validate-on="blur|input"
                  :danger="errors.has($t('EmailLabel'))"
                  :danger-text="errors.first($t('EmailLabel'))"
                  val-icon-danger="clear"/>
              </div>
              <div class="vx-col w-full mt-5 md:mt-0 md:w-1/2 md:pl-2">
                <label class="vs-input--label">
                  {{$t('PhoneLabel')}}
                </label>
                <vue-tel-input
                  v-model="phoneNational"
                  :disabledFetchingCountry="true"
                  :dynamicPlaceholder="false"
                  :enabledCountryCode="true"
                  :validCharactersOnly="true"
                  :preferredCountries="['AU', 'CO']"
                  :defaultCountry="codeCountry ? codeCountry : 'AU'"
                  autocomplete="tel"
                  @validate="(payload) => (this.phoneValidate = payload)"
                  @input="() => (this.phoneInput = true)"
                  @blur="() => (this.phoneInput = true)">
                </vue-tel-input>
              </div>
            </div>

            <div v-show="contactValidationError" class="text-danger text-sm mt-3">
              {{ $t('EmailOrPhoneRequired') }}
            </div>
              </fieldset>

              <!-- Marketing consent checkbox - only for 4+ stars (happy customers) -->
              <div v-if="rating > 3" class="vx-row mt-5">
                <div class="vx-col w-full">
                  <vs-checkbox
                    v-model="marketingConsent"
                    style="display: -webkit-inline-box !important; -webkit-box-align: start;"
                  >
                    {{ $t('MarketingConsentLabel') }}
                  </vs-checkbox>
                </div>
              </div>

              <!-- Privacy disclaimer -->
              <div class="vx-row mt-5">
                <div class="vx-col w-full text-justify md:text-left">
              <small>
                By submitting your feedback, you agree to our
                <a
                  v-if="policyType === $enums.Tenants.POLICY_TYPE.URL"
                  :href="linkPolicy"
                  target="_blank"
                  class="link-contextual privacy-policy-link text-gray-dark"
                >
                  privacy policy
                </a>
                <router-link
                  v-else
                  :to="`/privacy-policy/${$route.params.tenantUrlLinksSuffix}`"
                  target="_blank"
                  class="link-contextual font-bold privacy-policy-link text-gray-dark"
                  >
                  privacy policy
                </router-link>.
                <span v-if="rating <= 3">
                  We'll only use your details to respond to your feedback.
                </span>
                <span v-else>
                  We'll only use your details to respond to your feedback unless you opt in for marketing.
                </span>
                  </small>
                </div>
              </div>

              <!-- Option to switch back to Google Reviews for 4+ stars -->
              <div v-if="rating > 3 && showInternalForm" class="mt-3">
                <a @click="showInternalForm = false" class="cursor-pointer text-sm link-contextual">
                  {{ $t('LeaveGoogleReviewInstead') }}
                </a>
              </div>

              <vue-recaptcha
                v-if="!sent && rating > 0 && !contactToken && shouldShowForm"
                ref="recaptcha"
                class="flex justify-center mt-5 recaptcha md:justify-start"
                :class="[
                  recaptchaError ? 'error' : ''
                ]"
                :sitekey="recaptchaKey"
                @verify="onCaptchaVerify"
                @expired="onCaptchaExpired"
              ></vue-recaptcha>
              <div
                class="text-danger text-center md:text-left">
                <p
                  v-if="recaptchaError">
                  {{ $t('RecaptchaError') }}
                </p>
              </div>

              <div
                v-if="!sent && rating > 0 && shouldShowForm"
                class="text-center mt-5 md:text-left">
                <vs-button
                  :style="`background: ${accentColourLocal} !important;
                  color: ${accentContrastTextColor} !important`"
                  class="send-button w-full md:w-auto shadow-md"
                  @click="confirmReview">
                  {{ $t('ConfirmButtonText') }}
                </vs-button>
              </div>
            </div>
          </transition>
        </template>

      </form>
    </div>
    
    <powered-by-footer page-type="review"/>
  </div>
</template>

<script>
import StarRating from 'vue-star-rating';
import { VueRecaptcha } from 'vue-recaptcha';
import { VueTelInput } from 'vue-tel-input';

import reviewService from '@/api/modules/review.service';
import GoogleButton from '@/views/pages/_components/GoogleButton.vue';
import PoweredByFooter from '@/views/pages/_components/PoweredByFooter.vue';
import { getContrastColor } from '@/utils/colorUtils';
import HostedPageBanner from '@/components/HostedPageBanner.vue';

const recaptchaKey = process.env.VUE_APP_GOOGLE_RECAPTCHA_KEY;

export default {
  name: 'HostedInternalReviewForm',
  components: {
    StarRating,
    VueRecaptcha,
    VueTelInput,
    GoogleButton,
    PoweredByFooter,
    HostedPageBanner,
  },
  props: {
    mobilePreview: {
      type: Boolean,
      default: false,
    },
    desktopPreview: {
      type: Boolean,
      default: false,
    },
  },
  i18n: {
    messages: {
      en: {
        Title: 'How was your experience with {businessName}?',
        Subtitle: 'Your feedback helps us improve.',
        ConfirmButtonText: 'Submit',
        FeedbackPlaceholder: 'Share your feedback here',

        // Rating messages
        OnStarRating: 'Oh no! Let us know how we can do better.',
        TwoStarRating: "We're sorry to hear that. How can we improve?",
        ThreeStarRating: 'Thanks for the feedback. What could we do better?',
        FourStarRating: "Great! We're glad you had a good experience.",
        FiveStarRating: "Excellent! We're thrilled you loved it!",

        // Form labels
        FirstNameLabel: 'First name',
        LastNameLabel: 'Last name',
        EmailLabel: 'Email',
        PhoneLabel: 'Phone number',
        MarketingConsentLabel: 'Join our list for special offers and updates.',

        // Validation messages
        EmailOrPhoneRequired: 'Please provide either an email or a phone number.',

        // Privacy and links
        PrivacyDisclaimer: 'By submitting your feedback, you agree to our privacy policy.',
        LeaveInternalReviewInstead: 'Leave an internal review instead',
        LeaveGoogleReviewInstead: 'Leave a Google review instead',
        PrivacyPolicy: 'Privacy Policy',

        // Existing messages
        IssueFeedbackMsg: 'Oh no! Let us know how we can do better.',
        GoogleFeedbackMsg: "Great! We'd love to hear your thoughts.",
        ThanksMsgTitle: 'Thanks for your feedback',
        ThanksMsgSubtitle: 'We appreciate your honesty and will use your input to improve.',
        GoogleReviewMsg: 'Help others discover us by sharing your experience.',
        GoogleReviewButton: 'Leave a Google Review',
        RecaptchaError: 'Please confirm you are not a robot.',
      },
    },
  },
  data() {
    return {
      brandNameLocal: '',
      iconPathLocal: '',
      logoPathLocal: '',
      preferLogoOverIconLocal: false,
      brandColourLocal: '',
      accentColourLocal: '',

      policyType: '',
      inlinePolicy: '',
      linkPolicy: '',
      googleReviewLink: '',

      rating: 0,
      feedback: '',

      // New fields for contact information
      firstName: '',
      lastName: '',
      email: '',
      phoneNational: '',
      codeCountry: '',
      phoneValidate: null,
      phoneInput: false,
      marketingConsent: false,

      // Form state
      showInternalForm: false,
      sent: false,
      confirmAttempt: false,

      tenantUrlLinksSuffix: null,
      contactToken: null,

      recaptchaKey,
      recaptchaToken: '',
      recaptchaError: false,
    };
  },
  computed: {
    brandContrastTextColor() {
      return this.brandColourLocal ? this.getContrastColor(this.brandColourLocal) : '';
    },
    accentContrastTextColor() {
      return this.accentColourLocal ? this.getContrastColor(this.accentColourLocal) : '';
    },
    ratingMessage() {
      if (this.rating === 1) return this.$t('OnStarRating');
      if (this.rating === 2) return this.$t('TwoStarRating');
      if (this.rating === 3) return this.$t('ThreeStarRating');
      if (this.rating === 4) return this.$t('FourStarRating');
      if (this.rating === 5) return this.$t('FiveStarRating');
      return '';
    },
    shouldShowForm() {
      return (this.rating > 0 && this.rating <= 3)
        || (this.rating > 3 && this.showInternalForm)
        || (this.rating > 3 && !this.googleReviewLink && !this.showInternalForm);
    },
    contactValidationError() {
      if (!this.confirmAttempt) return false;

      const hasValidEmail = this.email.trim() && !this.errors.has(this.$t('EmailLabel'));
      const hasValidPhone = this.phoneNational.trim() && this.phoneValidate && this.phoneValidate.valid;

      return !hasValidEmail && !hasValidPhone;
    },
  },
  created() {
    // Add SEO meta tags to prevent search engine indexing
    this.addNoIndexMetaTags();

    const { tenantUrlLinksSuffix, contactToken } = this.$route.params;

    this.tenantUrlLinksSuffix = tenantUrlLinksSuffix || null;
    this.contactToken = contactToken || null;

    if (this.tenantUrlLinksSuffix) {
      this.fetchBrandingDetails();
    } else {
      this.$router.push({ name: 'not-authorized' });
    }

    if (this.contactToken) {
      this.fetchContactDetails();
    }
  },
  methods: {
    addNoIndexMetaTags() {
      // Remove existing meta tags if they exist
      const existingRobotsTag = document.querySelector('meta[name="robots"]');
      const existingGooglebotTag = document.querySelector('meta[name="googlebot"]');

      if (existingRobotsTag) {
        existingRobotsTag.remove();
      }
      if (existingGooglebotTag) {
        existingGooglebotTag.remove();
      }

      // Add noindex meta tags
      const robotsTag = document.createElement('meta');
      robotsTag.name = 'robots';
      robotsTag.content = 'noindex, nofollow';
      document.head.appendChild(robotsTag);

      const googlebotTag = document.createElement('meta');
      googlebotTag.name = 'googlebot';
      googlebotTag.content = 'noindex, nofollow';
      document.head.appendChild(googlebotTag);
    },
    async fetchBrandingDetails() {
      this.$vs.loading({
        type: 'radius',
      });

      try {
        const resp = await reviewService.getReviewBrandingDetails(this.tenantUrlLinksSuffix);
        const {
          name,
          icon,
          logo,
          preferLogoOverIcon,
          brandColour,
          accentColor,
          policyType,
          inlinePolicy,
          linkPolicy,
          googleReviewLink,
        } = resp.data;

        this.brandNameLocal = name;
        this.iconPathLocal = icon;
        this.logoPathLocal = logo;
        this.preferLogoOverIconLocal = preferLogoOverIcon;
        this.brandColourLocal = brandColour;
        this.accentColourLocal = accentColor;

        this.policyType = policyType;
        this.inlinePolicy = inlinePolicy;
        this.linkPolicy = linkPolicy;
        this.googleReviewLink = googleReviewLink;
      } catch (err) {
        // Invalid tenant - redirect to 404 page
        this.$router.push({ name: 'page-error-404' });
      } finally {
        this.$vs.loading.close();
      }
    },
    async fetchContactDetails() {
      this.$vs.loading({
        type: 'radius',
      });

      try {
        const resp = await reviewService.getReviewContactDetails(
          this.tenantUrlLinksSuffix,
          this.contactToken,
        );
        const {
          firstName,
          lastName,
          email,
          phoneNational,
          codeCountry,
        } = resp.data;

        this.firstName = firstName || '';
        this.lastName = lastName || '';
        this.email = email || '';
        this.phoneNational = phoneNational || '';
        this.codeCountry = codeCountry || '';
      } catch (err) {
        console.log(err);
      } finally {
        this.$vs.loading.close();
      }
    },

    getContrastColor,
    getPhoneInternationalE164() {
      if (this.phoneValidate
        && this.phoneValidate.isValid
        && this.phoneValidate.number
        && this.phoneValidate.number.e164) {
        return this.phoneValidate.number.e164;
      }

      return '';
    },
    ratingSelected() {
      // Reset form state when rating changes
      this.showInternalForm = false;
      this.sent = false;
      this.confirmAttempt = false;

      // Reset form fields
      this.feedback = '';
      this.marketingConsent = false;

      // Reset recaptcha
      if (this.$refs.recaptcha) {
        this.$refs.recaptcha.reset();
        this.recaptchaToken = '';
      }
    },
    async confirmReview() {
      this.confirmAttempt = true;

      // Validate form if showing internal form
      if (this.shouldShowForm) {
        // Validate required fields
        const isFormValid = await this.$validator.validateAll();

        // Custom validation is now handled by the `contactValidationError` computed property.
        if (!isFormValid || this.contactValidationError) {
          return;
        }
      }

      // Validate recaptcha for anonymous submissions
      if (!this.contactToken && !this.recaptchaToken) {
        this.recaptchaError = true;
        return;
      }

      this.$vs.loading({
        type: 'radius',
        color: this.accentColourLocal,
      });

      try {
        // Prepare the payload (keeping existing structure)
        let payload = {
          score: this.rating,
          review: this.feedback,
          recaptchaToken: this.recaptchaToken,
          contactToken: this.contactToken || '',
        };

        // Log new fields data for future backend integration
        if (this.shouldShowForm) {
          const phoneInternational = this.getPhoneInternationalE164();
          payload = {
            ...payload,
            firstName: this.firstName,
            lastName: this.lastName,
            email: this.email,
            marketingConsent: this.marketingConsent,
          };
          
          // Only include phoneInternational if it has a value
          if (phoneInternational) {
            payload.phoneInternational = phoneInternational;
          }
        }

        // Submit review using existing API
        await reviewService.postReview(payload, this.tenantUrlLinksSuffix);

        this.sent = true;
      } catch (err) {
        console.error(err);

        if (!this.contactToken) {
          this.onCaptchaExpired();
        }
      } finally {
        this.$vs.loading.close();
      }
    },
    goToGoogleReview() {
      window.open(this.googleReviewLink, '_blank');
    },
    onCaptchaVerify(response) {
      this.recaptchaToken = response;
      this.recaptchaError = false;
    },
    onCaptchaExpired() {
      this.recaptchaToken = '';
      if (this.$refs.recaptcha) {
        this.$refs.recaptcha.reset();
      }
    },
  },
};
</script>

<style lang="scss">


.internal-review-form {
  // Fix for mobile scrolling issues
  &.internal-review-form--full {
    // Use viewport helper variable with fallback
    min-height: 100vh;
    min-height: calc(var(--vh, 1vh) * 100);
    position: relative;
    
    // iOS specific height handling
    @supports (-webkit-touch-callout: none) {
      min-height: -webkit-fill-available;
    }
  }
  
  // Use flex-grow instead of flex-1 to avoid height calculation issues
  .flex-grow {
    flex-grow: 1;
  }
  // Style vue-tel-input with accent colors for all states
  .vue-tel-input {
    border: 1px solid rgba(0, 0, 0, 0.2) !important;
    border-radius: 5px !important;
    transition: all 0.15s ease !important;

    &:hover {
      border-color: v-bind('accentColourLocal') !important;
    }

    &:focus-within {
      border-color: v-bind('accentColourLocal') !important;
    }
    
    &:active {
      border-color: v-bind('accentColourLocal') !important;
    }

    .vti__input {
      border: none !important;
      outline: none !important;
      padding: 10px 12px !important;
      font-size: 0.875rem !important;
    }

    .vti__dropdown {
      border-right: 1px solid rgba(0, 0, 0, 0.1) !important;
      background: transparent !important;
      
      &:hover {
        background: rgba(0, 0, 0, 0.02) !important;
      }
    }

    .vti__dropdown-list {
      z-index: 9999 !important;
    }
  }

  .recaptcha.error > div {
    border: 1px solid #f56565 !important;
    border-radius: 4px;
  }

  // Checkbox styling with dynamic accent color and contrast tick
  .vs-checkbox--check {
    background: v-bind('accentColourLocal') !important;
  }
  
  // Force tick color to use accent contrast color
  .vs-checkbox--icon {
    color: v-bind('accentContrastTextColor') !important;
  }
  
  // Checkbox border on checked state
  .vs-checkbox-primary input:checked + .vs-checkbox {
    border: 2px solid v-bind('accentColourLocal') !important;
  }
  
  // Checkbox hover state
  .vs-checkbox:hover {
    border-color: v-bind('accentColourLocal') !important;
  }

  .checkbox_x.vs-checkbox {
    @apply w-5 h-5;
  }

  .vs-inputx {
    min-height: 44px !important;
  }

  // Button hover and active states
  .send-button {
    transition: all 0.2s ease !important;
    
    &:hover {
      border: 1px solid v-bind('accentColourLocal') !important;
      box-shadow: 0 8px 25px -8px v-bind('accentColourLocal') !important;
      transform: translateY(-2px);
    }
    
    &:active {
      transform: translateY(0);
      box-shadow: 0 4px 15px -6px v-bind('accentColourLocal') !important;
    }
  }

  // VS Input field styling with accent colors
  .vs-input {
    &:hover .vs-inputx {
      border-color: v-bind('accentColourLocal') !important;
    }
    
    &.input-primary-focus .vs-inputx {
      border-color: v-bind('accentColourLocal') !important;
    }
    
    &.input-primary-active .vs-inputx {
      border-color: v-bind('accentColourLocal') !important;
    }
  }
  
  // Override default vs-input focus styles
  .vs-input-primary .vs-input--input:focus {
    border: 1px solid v-bind('accentColourLocal') !important;
  }
  
  // Prevent label color changes on focus
  .vs-input-primary.isFocus .vs-input--label,
  .vs-input-primary.vs-input--input:focus .vs-input--label {
    color: inherit !important;
  }

  // Textarea styling with accent colors
  .vs-con-textarea {
    transition: all 0.15s ease !important;
    
    &:hover {
      border-color: v-bind('accentColourLocal') !important;
    }
    
    &:focus-within {
      border-color: v-bind('accentColourLocal') !important;
    }

    textarea {
      height: 100px;
      resize: none !important;
      
      &:focus {
        outline: none !important;
        border-color: v-bind('accentColourLocal') !important;
      }
    }
  }
  
  // Privacy policy link styling
  .privacy-policy-link {
    color: rgba(var(--vs-grey-dark), 1) !important;
    text-decoration: underline;
    
    &:hover {
      opacity: 0.8;
    }
  }
  
  // Fieldset border styling for contact validation
  fieldset {
    &.border-grey-light {
      border-color: rgba(0, 0, 0, 0.2) !important;
    }
    
    &:hover:not(.border-danger) {
      border-color: v-bind('accentColourLocal') !important;
    }
  }

  .vue-star-rating {
    column-gap: 12px !important;

    .vue-star-rating-star {
      filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.1));
      transition: transform 0.2s ease;
      
      &:hover {
        transform: scale(1.1);
      }
    }
  }

  // Transition animations for smooth layout changes
  .stars-container {
    transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    
    &.transform-center {
      padding-top: 0;
    }
    
    &.transform-top {
      padding-top: 2rem;
    }
  }

  // Improved fade and slide transition for all sections
  .fade-slide-enter-active {
    transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }
  
  .fade-slide-leave-active {
    transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }

  // Vue 2 transition classes
  .fade-slide-enter,
  .fade-slide-leave-to {
    opacity: 0;
    transform: translateY(10px);
  }

  .fade-slide-enter-to,
  .fade-slide-leave {
    opacity: 1;
    transform: translateY(0);
  }

  // Ensure content maintains minimum height during transition
  .animate-content {
    min-height: 50px;
    transition: min-height 0.3s ease;
  }

  // Specific styling for form content to prevent layout jumps
  .form-content {
    overflow: hidden;
    animation: fadeIn 0.3s ease-out forwards;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  // Ensure smooth transition for the main form container
  form {
    transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  }
}
</style>
